import { useMutation } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';
import axios from 'axios';
import { FILE_CHUNK_SIZE } from '@/stack-shared/constants/resource-constants';
import { IInitPresignedUrlMultipartUploadPayload, IInitPresignedUrlSimpleUploadPayload, CompletedPart } from '@/stack-shared/interfaces/general/aws-interfaces';

const httpClient = new HttpClient();

interface GetS3UploadUrlPayload {
  fileName: string;
  fileSize: number;
  mimeType: string;
}

const getS3UploadUrlApi = async (payload: GetS3UploadUrlPayload): Promise<any> => {
  const response = await httpClient.post('api/document/get-s3-upload-url', payload);
  return response.data;
};

const uploadMultipartFile = async (
  file: File,
  urls: string[],
  setProgress?: (percent: number) => void
): Promise<CompletedPart[]> => {
  const httpClient = axios.create();
  delete httpClient.defaults.headers.put['Content-Type'];

  const fileSize = file.size;
  const resParts: CompletedPart[] = [];
  let partNumber = 0;

  for (let start = 0; start < fileSize; start += FILE_CHUNK_SIZE) {
    let end = start + FILE_CHUNK_SIZE;
    if (end > fileSize) {
      end = fileSize;
    }
    const blob = file.slice(start, end);

    const part = await httpClient.put(urls[partNumber], blob, {
      onUploadProgress: (progressEvent) => {
        const { loaded } = progressEvent;
        const totalLoaded = loaded + FILE_CHUNK_SIZE * partNumber;
        const percent = Math.floor((totalLoaded / fileSize) * 100);
        if (setProgress) {
          setProgress(percent);
        }
      },
    });

    partNumber += 1;

    resParts.push({
      ETag: (part as any).headers.etag,
      PartNumber: partNumber,
    });
  }

  return resParts;
};

const uploadSimpleFile = async (
  file: File,
  url: string,
  setProgress?: (percent: number) => void
): Promise<unknown> => {
  let result = null;
  try {
    result = await axios.put(url, file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      onUploadProgress: (progressEvent) => {
        const { loaded, total } = progressEvent;
        const percentage = Math.floor((loaded * 100) / total!);
        if (setProgress) {
          setProgress(percentage);
        }
      },
    });
  } catch (e) {
    console.log(e);
    throw e;
  }

  return result;
};

export const useGetS3UploadUrl = () => {
  return useMutation({
    mutationFn: getS3UploadUrlApi,
  });
};

export const useInitPresignedUrlMultipartUpload = () => {
  return useMutation({
    mutationFn: async (payload: IInitPresignedUrlMultipartUploadPayload & { onProgress?: (percent: number) => void }) => {
      const { onProgress, ...uploadPayload } = payload;
      return uploadMultipartFile(uploadPayload.file, uploadPayload.urls, onProgress);
    },
  });
};

export const useInitPresignedUrlSimpleUpload = () => {
  return useMutation({
    mutationFn: async (payload: IInitPresignedUrlSimpleUploadPayload & { onProgress?: (percent: number) => void }) => {
      const { onProgress, ...uploadPayload } = payload;
      return uploadSimpleFile(uploadPayload.file, uploadPayload.url, onProgress);
    },
  });
};

export const useCompleteMultipartUpload = () => {
  return useMutation({
    mutationFn: async (payload: { key: string; uploadId: string; parts: CompletedPart[] }): Promise<any> => {
      // This would typically call a backend endpoint to complete the multipart upload
      // For now, we'll just return the payload as the backend handles this
      const response = await httpClient.post('api/aws/complete-multipart-upload', payload);
      return response.data;
    },
  });
};
